// /admin/lavagem — Instruções de lavagem, copia estrutural e visual de
// AdminTechnicalitiesPage.tsx (mesmo esqueleto: PageHeader + Card + tabela +
// Dialog + toast + invalidateQueries), com icone (placa clara fixa,
// reaproveitando TechnicalityIcon — componente generico, nao especifico de
// tecnicidades), ordem por arrastar (dnd-kit) e "Republicar produtos" por
// linha. Contrato do backend: technicalit -> care_instruction nos campos.
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, RefreshCw, Trash2, Upload } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, apiErrorMessage } from "../../lib/api";
import type { RepublishCareInstructionResponse, CareInstructionAdmin } from "../../lib/types";
import { PageHeader } from "../../components/layout/PageHeader";
import { TechnicalityIcon } from "../../components/TechnicalityIcon";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/ui/empty-state";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";

const QUERY_KEY = ["admin-care-instructions"];

export function AdminCareInstructionsPage(): JSX.Element {
  const qc = useQueryClient();
  const {
    data: items = [],
    isLoading,
    isError,
    error,
  } = useQuery<CareInstructionAdmin[]>({
    queryKey: QUERY_KEY,
    // GET /api/admin/care-instructions devolve { items: [...] } (mesmo formato
    // de GET /api/admin/technicalities) — igual ao GET /api/care-instructions.
    queryFn: async () =>
      (await api.get<{ items: CareInstructionAdmin[] }>("/api/admin/care-instructions")).data.items,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CareInstructionAdmin | null>(null);
  // Handoff §Instruções de lavagem: mobile so tem a galeria; a lista (com drag de
  // ordem) e exclusiva do desktop. "view" so importa em telas >=md — em
  // mobile a galeria aparece sempre, independente do estado.
  const [view, setView] = useState<"grid" | "list">("grid");

  // ordem local otimista, sincronizada quando o conjunto de ids muda (mesmo
  // padrao do PhotosTab: preserva a ordem local, so acrescenta o que e novo).
  const [order, setOrder] = useState<number[]>([]);
  const idsKey = useMemo(() => items.map((i) => i.id).join(","), [items]);
  const [syncedKey, setSyncedKey] = useState("");
  if (syncedKey !== idsKey) {
    const incoming = items.map((i) => i.id);
    const incomingSet = new Set(incoming);
    const preserved = order.filter((id) => incomingSet.has(id));
    const fresh = incoming.filter((id) => !preserved.includes(id));
    setOrder([...preserved, ...fresh]);
    setSyncedKey(idsKey);
  }

  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const orderMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      await api.put("/api/admin/care-instructions/order", { ids });
    },
    onSuccess: () => {
      toast.success("Ordem salva");
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const onDragEnd = (e: DragEndEvent): void => {
    if (!e.over || e.over.id === e.active.id) return;
    const activeId = Number(e.active.id);
    const overId = Number(e.over.id);
    setOrder((curr) => {
      const oldIdx = curr.indexOf(activeId);
      const newIdx = curr.indexOf(overId);
      if (oldIdx < 0 || newIdx < 0) return curr;
      const next = arrayMove(curr, oldIdx, newIdx);
      orderMutation.mutate(next);
      return next;
    });
  };

  const delMut = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/admin/care-instructions/${id}`);
    },
    onSuccess: () => {
      toast.success("Instrução de lavagem excluída");
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => {
      if (
        (err as { response?: { status?: number; data?: { details?: { skus?: number; publications?: number } } } })
          ?.response?.status === 409
      ) {
        const details = (err as { response?: { data?: { details?: { skus?: number; publications?: number } } } })
          .response?.data?.details;
        const skus = details?.skus ?? 0;
        toast.error(
          `Em uso por ${skus} produto${skus === 1 ? "" : "s"} — desative em vez de excluir, ou desvincule antes.`,
        );
        return;
      }
      toast.error(apiErrorMessage(err));
    },
  });

  const republishMut = useMutation({
    mutationFn: async (id: number) =>
      (await api.post<RepublishCareInstructionResponse>(`/api/admin/care-instructions/${id}/republish`)).data,
    onSuccess: (d) => {
      toast.success(
        `${d.enqueued} publicação${d.enqueued === 1 ? "" : "ões"} enfileirada${d.enqueued === 1 ? "" : "s"}` +
          (d.skipped_already_queued > 0 ? ` (${d.skipped_already_queued} já estavam na fila)` : ""),
      );
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div>
      <PageHeader
        title="Instruções de lavagem"
        description="Ícones + título + descrição que aparecem no site (aba na loja, quando o canal manda por lá)."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="icon" aria-hidden /> Nova instrução de lavagem
          </Button>
        }
      />

      {isLoading ? (
        <div className="card stack">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : isError ? (
        <EmptyState title="Erro" description={apiErrorMessage(error)} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhuma instrução de lavagem cadastrada"
          description="Cadastre a primeira (ex.: Secagem rápida, Proteção UV)."
          action={<Button onClick={() => setCreateOpen(true)}>Criar primeira instrução de lavagem</Button>}
        />
      ) : (
        <div className="stack">
          <div className="cluster">
            <div
              className="hidden md:inline-flex"
              style={{
                background: "var(--color-card)",
                border: ".0625rem solid var(--color-line)",
                borderRadius: "var(--radius-md)",
                padding: ".1875rem",
                gap: ".125rem",
              }}
            >
              <button
                type="button"
                onClick={() => setView("grid")}
                style={{
                  border: 0,
                  borderRadius: "var(--radius-sm)",
                  height: "1.75rem",
                  padding: "0 .75rem",
                  fontSize: ".8125rem",
                  fontWeight: 800,
                  cursor: "pointer",
                  background: view === "grid" ? "var(--color-coral)" : "transparent",
                  color: view === "grid" ? "#fff" : "var(--color-ink-2)",
                }}
              >
                Galeria
              </button>
              <button
                type="button"
                onClick={() => setView("list")}
                style={{
                  border: 0,
                  borderRadius: "var(--radius-sm)",
                  height: "1.75rem",
                  padding: "0 .75rem",
                  fontSize: ".8125rem",
                  fontWeight: 800,
                  cursor: "pointer",
                  background: view === "list" ? "var(--color-coral)" : "transparent",
                  color: view === "list" ? "#fff" : "var(--color-ink-2)",
                }}
              >
                Lista
              </button>
            </div>
            <span style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--color-ink-2)" }}>
              {items.length} instruç{items.length === 1 ? "ão" : "ões"} de lavagem · ícone SVG/PNG até 512 KB
            </span>
          </div>

          {/* Galeria — sempre visivel no mobile; no desktop so quando view==="grid" */}
          <div className={cn("cards", view === "list" && "md:hidden")}>
            {order.map((id) => {
              const t = byId.get(id);
              if (!t) return null;
              return (
                <CareInstructionCard
                  key={id}
                  item={t}
                  onEdit={() => setEditing(t)}
                  onDelete={() => {
                    if (confirm(`Excluir a instrução de lavagem "${t.title}"?`)) delMut.mutate(t.id);
                  }}
                  deleting={delMut.isPending}
                />
              );
            })}
          </div>

          {/* Lista com drag de ordem — exclusiva do desktop */}
          {view === "list" ? (
            <div className="card hidden md:block" style={{ padding: "0 1rem" }}>
              <div
                className="row row__head"
                style={{ gridTemplateColumns: "1.25rem 3rem minmax(0,3fr) minmax(0,5fr) minmax(3.5rem,1.5fr) minmax(5rem,1.5fr) minmax(6rem,2fr)" }}
              >
                <span />
                <span>Ícone</span>
                <span>Título</span>
                <span>Descrição</span>
                <span>Ativo</span>
                <span>Produtos</span>
                <span style={{ textAlign: "right" }}>Ações</span>
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={order} strategy={verticalListSortingStrategy}>
                  {order.map((id) => {
                    const t = byId.get(id);
                    if (!t) return null;
                    return (
                      <CareInstructionRow
                        key={id}
                        item={t}
                        onEdit={() => setEditing(t)}
                        onDelete={() => {
                          if (confirm(`Excluir a instrução de lavagem "${t.title}"?`)) delMut.mutate(t.id);
                        }}
                        onRepublish={() => {
                          if (
                            confirm(
                              `Enfileirar republish de ${t.usage_count} publicaç${t.usage_count === 1 ? "ão" : "ões"}?`,
                            )
                          ) {
                            republishMut.mutate(t.id);
                          }
                        }}
                        deleting={delMut.isPending}
                        republishing={republishMut.isPending}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            </div>
          ) : null}
        </div>
      )}

      <CareInstructionDialog
        key={editing?.id ?? "new"}
        open={createOpen || editing !== null}
        editing={editing}
        onOpenChange={(o) => {
          if (!o) {
            setCreateOpen(false);
            setEditing(null);
          }
        }}
      />
    </div>
  );
}

function CareInstructionCard({
  item,
  onEdit,
  onDelete,
  deleting,
}: {
  item: CareInstructionAdmin;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}): JSX.Element {
  return (
    <div className="card stack" style={{ alignItems: "flex-start" }}>
      <TechnicalityIcon iconUrl={item.icon_url} title={item.title} size="xl" />
      <div style={{ minWidth: 0, width: "100%" }}>
        <div style={{ fontSize: ".875rem", fontWeight: 900, lineHeight: 1.25 }}>{item.title}</div>
        <div className="clamp-3" style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--color-ink-2)", marginTop: ".25rem" }}>
          {item.description || "—"}
        </div>
      </div>
      <div className="cluster">
        <Badge variant={item.active ? "live" : "outline"}>{item.active ? "Ativo" : "Inativo"}</Badge>
        <span style={{ fontSize: ".6875rem", fontWeight: 700, color: "var(--color-ink-3)" }}>
          {item.usage_count} produto{item.usage_count === 1 ? "" : "s"}
        </span>
      </div>
      <div className="cluster" style={{ width: "100%" }}>
        <Button variant="secondary" size="sm" className="grow" onClick={onEdit}>
          Editar ícone
        </Button>
        <Button variant="destructive" size="icon" onClick={onDelete} disabled={deleting} aria-label="Excluir">
          <Trash2 className="icon" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

function CareInstructionRow({
  item,
  onEdit,
  onDelete,
  onRepublish,
  deleting,
  republishing,
}: {
  item: CareInstructionAdmin;
  onEdit: () => void;
  onDelete: () => void;
  onRepublish: () => void;
  deleting: boolean;
  republishing: boolean;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    gridTemplateColumns: "1.25rem 3rem minmax(0,3fr) minmax(0,5fr) minmax(3.5rem,1.5fr) minmax(5rem,1.5fr) minmax(6rem,2fr)",
    padding: "0",
  };
  return (
    <div ref={setNodeRef} style={style} className="row">
      <span
        {...attributes}
        {...listeners}
        style={{ color: "var(--color-ink-3)", cursor: "grab", fontSize: ".875rem", textAlign: "center" }}
      >
        ⠿
      </span>
      <span>
        <TechnicalityIcon iconUrl={item.icon_url} title={item.title} size="sm" />
      </span>
      <span className="row__cell" style={{ fontWeight: 900, whiteSpace: "normal" }}>{item.title}</span>
      <span className="row__cell truncate" style={{ color: "var(--color-ink-2)" }} title={item.description}>
        {item.description || "—"}
      </span>
      <span>
        <Badge variant={item.active ? "live" : "outline"}>{item.active ? "Ativo" : "Inativo"}</Badge>
      </span>
      <span style={{ fontSize: ".75rem", fontWeight: 700, color: "var(--color-ink-3)" }}>
        {item.usage_count} produto{item.usage_count === 1 ? "" : "s"}
      </span>
      <span className="cluster" style={{ justifyContent: "flex-end" }}>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRepublish}
          disabled={republishing || item.usage_count === 0}
          title={
            item.usage_count === 0
              ? "Nenhum produto usa esta instrução de lavagem ainda"
              : `Republicar as ${item.usage_count} publicações que usam esta instrução de lavagem`
          }
        >
          <RefreshCw className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          Editar
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} disabled={deleting}>
          <Trash2 className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
        </Button>
      </span>
    </div>
  );
}

function CareInstructionDialog({
  open,
  editing,
  onOpenChange,
}: {
  open: boolean;
  editing: CareInstructionAdmin | null;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const isEdit = editing !== null;
  const [title, setTitle] = useState(editing?.title ?? "");
  const [description, setDescription] = useState(editing?.description ?? "");
  const [active, setActive] = useState(editing?.active ?? true);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(editing?.icon_url ?? null);

  useEffect(() => {
    return () => {
      if (iconFile && previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iconFile]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = { title, description, active };
      let id = editing?.id;
      if (isEdit && id) {
        await api.put(`/api/admin/care-instructions/${id}`, body);
      } else {
        const r = await api.post<{ id: number }>("/api/admin/care-instructions", body);
        id = r.data.id;
      }
      if (iconFile && id) {
        const form = new FormData();
        form.append("icon", iconFile);
        // achado 3 do plano: o axios manda Content-Type: application/json por
        // padrao — precisa apagar aqui para o navegador inserir o boundary do
        // multipart sozinho, senao o servidor recebe corpo ilegivel.
        await api.post(`/api/admin/care-instructions/${id}/icon`, form, {
          headers: { "Content-Type": undefined },
        });
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? "Instrução de lavagem atualizada" : "Instrução de lavagem criada");
      void qc.invalidateQueries({ queryKey: QUERY_KEY });
      onOpenChange(false);
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} key={editing?.id ?? "new"}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar instrução de lavagem" : "Nova instrução de lavagem"}</DialogTitle>
          <DialogDescription>
            Título e descrição aparecem na loja. O ícone é SVG ou PNG, até 512 KB.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveMutation.mutate();
          }}
          className="stack"
          style={{ gap: "var(--sp-3)" }}
        >
          <div className="cluster" style={{ gap: "var(--sp-3)" }}>
            <TechnicalityIcon iconUrl={previewUrl} title={title || "?"} size="lg" />
            <label className="btn btn--secondary" style={{ cursor: "pointer" }}>
              <Upload className="icon" style={{ width: ".875rem", height: ".875rem" }} aria-hidden />
              {iconFile ? iconFile.name : "Escolher ícone (SVG ou PNG)"}
              <input
                type="file"
                accept=".svg,.png,image/svg+xml,image/png"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setIconFile(f);
                  if (f) setPreviewUrl(URL.createObjectURL(f));
                }}
              />
            </label>
          </div>
          <div>
            <Label htmlFor="tech-title">Título</Label>
            <Input id="tech-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="tech-desc">Descrição</Label>
            <Textarea
              id="tech-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <Switch checked={active} onChange={(e) => setActive(e.target.checked)} label="Ativa" />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" loading={saveMutation.isPending}>
              {isEdit ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
